import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, finalize, throwError } from 'rxjs';
import { LoadingIndicatorService } from '../services/loading-indicator.service';
import { SKIP_GLOBAL_LOADING } from './loading-context';

/**
 * Patterns for URLs that should not trigger the global loading indicator
 * - Polling endpoints (frequent background updates)
 * - Health checks
 * - Analytics/tracking
 */
const EXCLUDED_URL_PATTERNS = [
  /\/inbox-tasks.*\?poll=true/i,
  /\/notifications.*\?poll=true/i,
  /\/threads.*\?poll=true/i,
  /\/utility-dock-summary/i,
  /\/health/i,
  /\/analytics/i,
  /\/client-error-logs/i,
];

/**
 * Should this request trigger the global loading indicator?
 */
function shouldShowLoading(url: string): boolean {
  return !EXCLUDED_URL_PATTERNS.some(pattern => pattern.test(url));
}

/**
 * Global HTTP loading interceptor
 * 
 * Tracks pending HTTP requests and updates the global loading indicator.
 * Excluded endpoints (polling, health checks) won't trigger the indicator.
 * 
 * @example
 * // In app.config.ts
 * provideHttpClient(
 *   withInterceptors([authInterceptor, loadingInterceptor])
 * )
 */
export const loadingInterceptor: HttpInterceptorFn = (req, next) => {
  const loadingService = inject(LoadingIndicatorService);
  
  if (!req.context.get(SKIP_GLOBAL_LOADING) && shouldShowLoading(req.url)) {
    loadingService.startLoading('http');
    
    return next(req).pipe(
      catchError(error => {
        // On hard failures, force-reset the indicator state
        loadingService.reset();
        return throwError(() => error);
      }),
      finalize(() => loadingService.stopLoading('http'))
    );
  }
  
  return next(req);
};
